import { type CrossingEvent, type CrossingDirection, type VehicleClass } from '@urbanrats/contracts';
import { canonicalJson } from './hash.ts';
import { fromUtcInstant } from './time.ts';

export type CountWindow =
  | { kind: 'CAPTURE_TIME'; startMs: number; endMs: number }
  | { kind: 'MEDIA_PTS'; sourceEpoch: string; startMicros: number; endMicros: number };

export interface CountRules {
  gameRoundId: string;
  lineId: string;
  countedDirection: CrossingDirection;
  includedClasses: readonly VehicleClass[];
  window: CountWindow;
}

export type CrossingDecision =
  | 'COUNTED'
  | 'DUPLICATE_EVENT'
  | 'DUPLICATE_TRACK'
  | 'WRONG_ROUND'
  | 'WRONG_LINE'
  | 'WRONG_DIRECTION'
  | 'EXCLUDED_CLASS'
  | 'OUTSIDE_WINDOW'
  | 'SOURCE_EPOCH_MISMATCH'
  | 'NO_CAPTURE_TIME';

export interface CountResult {
  count: number;
  countedEventIds: string[];
  decisions: Map<string, CrossingDecision>;
  /** An uncertain event that affected (or could affect) counting needs human review. */
  requiresReview: boolean;
  /** Uncertain events currently counted (each might be spurious). */
  uncertainCounted: number;
  /** Uncertain events currently not counted that might be real (duplicate/class doubt). */
  uncertainExcluded: number;
  /** Same eventId seen with a different payload. */
  conflicts: number;
}

function inWindow(e: CrossingEvent, w: CountWindow): CrossingDecision | null {
  if (w.kind === 'CAPTURE_TIME') {
    if (e.capturedAt === null) return 'NO_CAPTURE_TIME';
    const t = fromUtcInstant(e.capturedAt);
    return t >= w.startMs && t < w.endMs ? null : 'OUTSIDE_WINDOW';
  }
  if (e.framePts.sourceEpoch !== w.sourceEpoch || e.sourceEpoch !== w.sourceEpoch) return 'SOURCE_EPOCH_MISMATCH';
  const p = e.framePts.ptsMicros;
  return p >= w.startMicros && p < w.endMicros ? null : 'OUTSIDE_WINDOW';
}

/**
 * Pure recount from the event log. Idempotent: replaying the same eventId never changes the count,
 * and one track counts at most once per round/line/direction. Window is half-open [start, end).
 */
export function countCrossings(events: readonly CrossingEvent[], rules: CountRules): CountResult {
  const decisions = new Map<string, CrossingDecision>();
  const countedTracks = new Set<string>();
  const seen = new Map<string, string>();
  const counted: string[] = [];
  let requiresReview = false;
  let uncertainCounted = 0;
  let uncertainExcluded = 0;
  let conflicts = 0;
  for (const e of events) {
    const body = canonicalJson(e);
    const prior = seen.get(e.eventId);
    if (prior !== undefined) {
      // Exact replay is a no-op; the same id with a different payload is a conflict for review.
      if (prior !== body) {
        requiresReview = true;
        conflicts++;
      }
      continue;
    }
    seen.set(e.eventId, body);
    let d: CrossingDecision;
    if (e.gameRoundId !== rules.gameRoundId) d = 'WRONG_ROUND';
    else if (e.lineId !== rules.lineId) d = 'WRONG_LINE';
    else if (e.direction !== rules.countedDirection) d = 'WRONG_DIRECTION';
    else if (!rules.includedClasses.includes(e.vehicleClass)) d = 'EXCLUDED_CLASS';
    else {
      const w = inWindow(e, rules.window);
      if (w) d = w;
      else {
        const trackKey = `${e.sourceEpoch}|${e.trackId}|${e.lineId}|${e.direction}`;
        if (countedTracks.has(trackKey)) d = 'DUPLICATE_TRACK';
        else {
          countedTracks.add(trackKey);
          d = 'COUNTED';
        }
      }
    }
    decisions.set(e.eventId, d);
    if (d === 'COUNTED') counted.push(e.eventId);
    if (e.uncertain && d === 'COUNTED') {
      requiresReview = true;
      uncertainCounted++;
    }
    if (e.uncertain && (d === 'DUPLICATE_TRACK' || d === 'EXCLUDED_CLASS')) {
      requiresReview = true;
      uncertainExcluded++;
    }
  }
  return { count: counted.length, countedEventIds: counted, decisions, requiresReview, uncertainCounted, uncertainExcluded, conflicts };
}
